/**
 * Auth Process
 * 
 * Headless process that manages authentication state and spawns login/register views when needed.
 * Runs continuously (KEEP_ALIVE) and spawns views on demand.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority, ProcessLifecycleMode } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { handleFetchError, handleHTTPError } from '../../services/error-handler/index.js';
import { setState } from '../../core/state.js';
import {
  getJWTToken,
  saveJWTToken,
  removeJWTToken,
  getUserData,
  saveUserData,
  removeUserData,
  isAuthenticated,
  clearAuthData,
  refreshToken,
  type UserData
} from '../../services/auth/token-storage.js';
import { AUTH_API_ENDPOINTS, OAUTH_CALLBACK_URL } from '../../services/auth/config.js';
import {
  designTokens,
  createHeading,
  createButton,
  createFormField,
  createTextInput,
  createCheckbox,
  escapeHtml as escapeHtmlUtil
} from '../../core/design-system.js';

interface AuthState {
  isAuthenticated: boolean;
  user: {
    email?: string;
    displayName?: string;
  } | null;
  error: string | null;
}

export class AuthProcess extends BaseProcess {
  kind: string = 'auth';

  metadata = {
    priority: ProcessPriority.SYSTEM,
    category: 'system' as const,
    canStop: false,
    preserveState: true,
    timeSensitive: false
  };

  private state: AuthState = {
    isAuthenticated: false,
    user: null,
    error: null
  };

  private authView: AuthView | null = null;
  private initialAuthLoad: Promise<void> | null = null;

  constructor(id: string) {
    super(id);
    // Check for existing auth state (refresh cookie, memory, etc.)
    this.initialAuthLoad = this.loadAuthState();
  }

  private async loadAuthState(): Promise<void> {
    // Load user data from localStorage (non-sensitive)
    const userData = getUserData();

    // Attempt to refresh JWT token using secure HttpOnly cookie if no token in memory
    if (!getJWTToken()) {
      await refreshToken();
    }

    if (isAuthenticated()) {
      this.state.isAuthenticated = true;
      this.state.user = userData || { email: undefined, displayName: undefined };
      
      // Sync to global state
      if (userData) {
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });
      }
    } else {
      this.state.isAuthenticated = false;
      this.state.user = null;
      
      // Clear global user state
      setState('user', {});
    }
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[AuthProcess] Starting auth process ${this.id}`);

    // Listen for auth-related events
    bus.on('auth.login.required', (payload) => {
      const source = (payload as { source?: string })?.source ?? 'bus.auth.login.required';
      this.showLogin(ctx, source);
    });

    bus.on('auth.register.required', (payload) => {
      const source = (payload as { source?: string; details?: Record<string, unknown> })?.source ?? 'bus.auth.register.required';
      log.debug('[AuthProcess] Received auth.register.required event', {
        processId: this.id,
        source,
        details: (payload as { details?: Record<string, unknown> })?.details
      });
      this.showRegister(ctx, source);
    });

    bus.on('auth.logout', () => {
      this.handleLogout(ctx);
    });

    const authInit = this.initialAuthLoad ?? Promise.resolve();
    authInit
      .catch(error => {
        log.error('[AuthProcess] Failed to initialize auth state', { error });
      })
      .finally(() => {
        this.handleInitialRoute(ctx);
        // Emit event that auth state has been loaded
        bus.emit('auth.state.loaded', { isAuthenticated: this.state.isAuthenticated });
      });
  }

  private handleInitialRoute(ctx: ProcessContext): void {
    // Check URL for routes and reset token
    const pathname = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('reset_token');
    const verificationToken = urlParams.get('verification_token');
    const magicToken = urlParams.get('magic_token');
    const oauthCode = urlParams.get('code');
    const oauthProvider = urlParams.get('provider');

    // Handle magic link token
    if (magicToken) {
      this.handleMagicLinkVerify(magicToken);
      // Clear URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('magic_token');
      window.history.replaceState({}, '', url.toString());
    } else if (oauthProvider && oauthCode) {
      // Handle OAuth callback (uses authorization code, not token)
      this.handleOAuthCallback(oauthCode, oauthProvider);
      // Clear URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('provider');
      window.history.replaceState({}, '', url.toString());
    } else if (verificationToken) {
      this.showLogin(ctx, 'auth-process:onStart-email-verification');
      this.handleEmailVerification(verificationToken);
      const url = new URL(window.location.href);
      url.searchParams.delete('verification_token');
      window.history.replaceState({}, '', url.toString());
    } else if (resetToken) {
      // Show reset password view if reset_token is in URL
      this.showResetPassword(ctx, 'auth-process:onStart-reset-token');
    } else if (pathname.includes('/register')) {
      this.showRegister(ctx, 'auth-process:onStart-route-check');
    } else if (pathname.includes('/forgot-password') || pathname.includes('/reset-password')) {
      this.showForgotPassword(ctx, 'auth-process:onStart-route-check');
    } else if (pathname.includes('/login')) {
      // Only show login if explicitly on /login route
      this.showLogin(ctx, 'auth-process:onStart-route-check');
    } else if (!this.state.isAuthenticated) {
      // Auto-show login view if not authenticated and no specific route
      this.showLogin(ctx, 'auth-process:onStart');
    } else {
      // User is authenticated - show logged-in status view
      this.showLoggedInStatus(ctx);
    }
  }

  private async handleOAuthCallback(code: string, provider: string): Promise<void> {
    log.debug(`[AuthProcess] Handling OAuth callback from ${provider} with authorization code`);

    try {
      // Exchange authorization code for JWT token (secure - no token in URL)
      const response = await fetch(AUTH_API_ENDPOINTS.oauthExchange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code, provider })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'OAuth Exchange');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.data) {
        const { token, user } = responseData.data;

        // Save JWT token and user data
        saveJWTToken(token);
        const userData: UserData = {
          email: user.email,
          displayName: user.displayName,
          userId: user.id
        };
        saveUserData(userData);

        this.state.isAuthenticated = true;
        this.state.user = {
          email: userData.email,
          displayName: userData.displayName
        };

        // Sync to global state
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });

        // Close auth view after successful login
        if (this.authView) {
          setTimeout(() => {
            bus.emit('process.view.dismiss', { processId: this.id, viewId: this.authView?.id });
            this.authView = null;
          }, 1500);
        }

        bus.emit('auth.login.success', { provider });
        log.debug(`[AuthProcess] OAuth login successful from ${provider}`);
      } else {
        throw new Error('Invalid response from OAuth exchange');
      }
    } catch (error) {
      log.error(`[AuthProcess] OAuth callback failed:`, { error, provider });
      bus.emit('auth.login.error', { error: 'OAuth callback failed', provider });
      this.authView?.setError('login', 'OAuth authentication failed. Please try again.');
    }
  }

  async handleMagicLinkSend(email: string): Promise<void> {
    log.debug(`[AuthProcess] Sending magic link to ${email}`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('login', null);
    this.authView?.setError('register', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.magicLinkSend, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Magic Link');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success) {
        this.authView?.setSuccess('login', 'Magic link sent! Check your email and click the link to sign in.');
        this.authView?.setSuccess('register', 'Magic link sent! Check your email and click the link to create your account.');
        bus.emit('auth.magic-link.sent', { email });
        log.debug(`[AuthProcess] Magic link sent successfully`);
      } else {
        throw new Error(responseData.error || 'Failed to send magic link');
      }
    } catch (error) {
      handleFetchError(error, 'Magic Link');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Magic link send failed:`, { error });
      bus.emit('auth.magic-link.error' as any, { error: errorMessage } as any);
      this.authView?.setError('login', errorMessage);
      this.authView?.setError('register', errorMessage);
    }
  }

  async handleMagicLinkVerify(token: string): Promise<void> {
    log.debug(`[AuthProcess] Verifying magic link token`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('login', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.magicLinkVerify, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token }),
        credentials: 'include' // Required for cookies to be set/received in cross-origin requests
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Magic Link Verification');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.data) {
        const { token: jwtToken, user } = responseData.data;

        // Save JWT token and user data
        saveJWTToken(jwtToken);
        const userData: UserData = {
          email: user.email,
          displayName: user.displayName,
          userId: user.id
        };
        saveUserData(userData);

        this.state.isAuthenticated = true;
        this.state.user = {
          email: userData.email,
          displayName: userData.displayName
        };

        // Sync to global state
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });

        // Show success message briefly before closing
        this.authView?.setSuccess('login', 'Welcome! You are now logged in.');

        // Close auth view after successful login
        if (this.authView) {
          // Small delay to show success message, then close
          setTimeout(() => {
            bus.emit('process.view.dismiss', { processId: this.id, viewId: this.authView?.id });
            this.authView = null;
          }, 1500);
        }

        bus.emit('auth.login.success', { provider: 'magic-link' });
        log.debug(`[AuthProcess] Magic link verification successful`);
      } else {
        throw new Error('Invalid response from magic link verification');
      }
    } catch (error) {
      handleFetchError(error, 'Magic Link Verification');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Magic link verification failed:`, { error });
      bus.emit('auth.login.error' as any, { error: errorMessage } as any);
      this.authView?.setError('login', errorMessage);
    }
  }

  async handleOAuthLogin(provider: 'google' | 'microsoft' | 'github' | 'x'): Promise<void> {
    log.debug(`[AuthProcess] Initiating OAuth login with ${provider}`);

    let authApiUrl: string;
    switch (provider) {
      case 'google':
        authApiUrl = AUTH_API_ENDPOINTS.googleAuthorize;
        break;
      case 'microsoft':
        authApiUrl = AUTH_API_ENDPOINTS.microsoftAuthorize;
        break;
      case 'github':
        authApiUrl = AUTH_API_ENDPOINTS.githubAuthorize;
        break;
      case 'x':
        authApiUrl = AUTH_API_ENDPOINTS.xAuthorize;
        break;
      default:
        authApiUrl = AUTH_API_ENDPOINTS.googleAuthorize;
    }

    // Add redirect_uri parameter
    const url = new URL(authApiUrl);
    url.searchParams.set('redirect_uri', OAUTH_CALLBACK_URL);

    // Redirect to OAuth provider
    window.location.href = url.toString();
  }

  private ensureAuthView(ctx: ProcessContext, mode: AuthViewMode): AuthView {
    if (!this.authView) {
      const view = new AuthView(
        this.id,
        `${this.id}-main`,
        this,
        mode
      );
      const viewInstance = view.toViewInstance();
      try {
        ctx.registerView(viewInstance);
        this.views.set(viewInstance.id, viewInstance);
        this.authView = view;
        log.debug(`[AuthProcess] Auth view spawned (mode=${mode})`);
      } catch (error) {
        log.error('[AuthProcess] Failed to register auth view', { error });
        this.views.delete(viewInstance.id);
        this.authView = null;
        throw error;
      }
    } else {
      this.authView.setMode(mode);
    }
    return this.authView;
  }

  private showLogin(ctx: ProcessContext, reason: string = 'unknown'): void {
    // Don't show login if user is already authenticated (unless explicitly requested via URL)
    if (this.state.isAuthenticated && !reason.includes('route-check')) {
      log.debug(`[AuthProcess] User already authenticated, skipping login view (reason=${reason})`);
      return;
    }
    log.debug(`[AuthProcess] Switching to login view (reason=${reason})`);
    this.ensureAuthView(ctx, 'login');
  }

  private showRegister(ctx: ProcessContext, reason: string = 'unknown'): void {
    log.debug(`[AuthProcess] Switching to register view (reason=${reason})`);
    this.ensureAuthView(ctx, 'register');
  }

  private showForgotPassword(ctx: ProcessContext, reason: string = 'unknown'): void {
    log.debug(`[AuthProcess] Switching to forgot password view (reason=${reason})`);
    this.ensureAuthView(ctx, 'forgot-password');
  }

  private showResetPassword(ctx: ProcessContext, reason: string = 'unknown'): void {
    log.debug(`[AuthProcess] Switching to reset password view (reason=${reason})`);
    this.ensureAuthView(ctx, 'reset-password');
  }

  private showLoggedInStatus(ctx: ProcessContext): void {
    log.debug(`[AuthProcess] Showing logged-in status view`);
    this.ensureAuthView(ctx, 'logged-in');
  }

  async handleLogin(email: string, password: string, rememberMe: boolean): Promise<void> {
    log.debug(`[AuthProcess] Attempting login for ${email}`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('login', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        // Handle HTTP error (will show toast notification and return error message)
        const errorMessage = await handleHTTPError(response, 'Login');

        // Try to get more details from response (response body already consumed by handleHTTPError, so clone first)
        let errorData: { error?: string } = { error: errorMessage };
        try {
          const clonedResponse = response.clone();
          errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
        } catch {
          // If cloning/parsing fails, use the message from handleHTTPError
          errorData = { error: errorMessage };
        }

        // Check if it's a password-related error (401 = invalid credentials)
        if (response.status === 401 || errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('invalid credentials')) {
          bus.emit('auth.password.invalid' as any, { reason: errorData.error || errorMessage });
        }

        throw new Error(errorData.error || errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      // Auth API returns { success: true, data: { token, user } }
      const data = responseData.data || responseData;

      // Save JWT token and user data
      if (data.token) {
        saveJWTToken(data.token);
        const userData: UserData = {
          email: data.user?.email || email,
          displayName: data.user?.displayName,
          userId: data.user?.id
        };
        saveUserData(userData);

        this.state.isAuthenticated = true;
        this.state.user = userData;

        // Sync to global state
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });

        // Close auth view after successful login
        if (this.authView) {
          setTimeout(() => {
            // Emit dismiss event to close the view
            bus.emit('process.view.dismiss', { processId: this.id, viewId: this.authView?.id });
            this.authView = null;
          }, 1500);
        }

        this.state.error = null;
        // Show success message briefly before closing
        this.authView?.setSuccess('login', 'Welcome back! You are now logged in.');

        bus.emit('auth.login.success', { email: userData.email });
        log.debug(`[AuthProcess] Login successful`);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      // Handle fetch error (will show toast notification)
      handleFetchError(error, 'Login');

      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Login failed:`, { error });
      bus.emit('auth.login.error' as any, { error: errorMessage } as any);

      // Update login view to show error
      this.authView?.setError('login', errorMessage);
    }
  }

  async handleRegister(email: string, password: string, confirmPassword: string): Promise<void> {
    log.debug(`[AuthProcess] Attempting registration for ${email}`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('register', null);

    if (password !== confirmPassword) {
      const error = 'Passwords do not match';
      this.state.error = error;
      bus.emit('auth.register.error', { error });
      this.authView?.setError('register', error);
      return;
    }

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        // Handle HTTP error (will show toast notification and return error message)
        const errorMessage = await handleHTTPError(response, 'Registration');

        // Try to get more details from response (response body already consumed by handleHTTPError, so clone first)
        let errorData: { error?: string } = { error: errorMessage };
        try {
          const clonedResponse = response.clone();
          errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
        } catch {
          // If cloning/parsing fails, use the message from handleHTTPError
          errorData = { error: errorMessage };
        }

        throw new Error(errorData.error || errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      // Auth API returns { success: true, data: { token, user } }
      const data = responseData.data || responseData;

      // Save JWT token and user data
      if (data.token) {
        saveJWTToken(data.token);
        const userData: UserData = {
          email: data.user?.email || email,
          displayName: data.user?.displayName,
          userId: data.user?.id
        };
        saveUserData(userData);

        this.state.isAuthenticated = true;
        this.state.user = userData;

        // Sync to global state
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });

        // Close register view
        this.authView?.setMode('login');

        this.state.error = null;
        bus.emit('auth.register.success', { email: userData.email });
        log.debug(`[AuthProcess] Registration successful`);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      // Handle fetch error (will show toast notification)
      handleFetchError(error, 'Registration');

      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Registration failed:`, { error });
      bus.emit('auth.register.error' as any, { error: errorMessage } as any);

      // Update register view to show error
      this.authView?.setError('register', errorMessage);
    }
  }

  async handleForgotPassword(email: string): Promise<void> {
    log.debug(`[AuthProcess] Attempting password reset for ${email}`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('forgot-password', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.forgotPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Password Reset');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Show success message
        (this.authView as any).forgotPasswordSuccess = true;
        (this.authView as any).forgotPasswordError = null;
        this.authView?.markDirty();
        log.debug(`[AuthProcess] Password reset email sent`);
      } else {
        throw new Error(data.error || 'Failed to send password reset email');
      }
    } catch (error) {
      handleFetchError(error, 'Password Reset');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Password reset failed:`, { error });
      this.authView?.setError('forgot-password', errorMessage);
    }
  }

  async handleEmailVerification(token: string): Promise<void> {
    log.debug('[AuthProcess] Verifying email via token');

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.verifyEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Email Verification');
        this.authView?.setError('login', errorMessage || 'Verification failed');
        return;
      }

      const data = await response.json();
      const message = data.message || 'Email verified successfully. You can now sign in.';

      this.authView?.setLoginSuccess(message);
    } catch (error) {
      handleFetchError(error, 'Email Verification');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.authView?.setError('login', errorMessage);
      log.error('[AuthProcess] Email verification failed:', { error });
    }
  }

  async handleResendVerification(email: string): Promise<void> {
    log.debug(`[AuthProcess] Resending verification for ${email}`);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.resendVerification, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Resend Verification');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const message = data.message || 'If this email exists, a verification link has been sent.';

      this.authView?.setLoginSuccess(message);
    } catch (error) {
      handleFetchError(error, 'Resend Verification');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.authView?.setError('login', errorMessage);
      log.error('[AuthProcess] Resend verification failed:', { error });
    }
  }

  async handleResetPassword(token: string, password: string): Promise<void> {
    log.debug(`[AuthProcess] Attempting to reset password with token`);

    // Clear previous error
    this.state.error = null;
    this.authView?.setError('reset-password', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.resetPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Password Reset');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Show success message
        (this.authView as any).resetPasswordSuccess = true;
        (this.authView as any).resetPasswordError = null;
        this.authView?.markDirty();

        // Clear token from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('reset_token');
        window.history.replaceState({}, '', url.toString());

        log.debug(`[AuthProcess] Password reset successful`);

        // After 2 seconds, switch to login
        // Note: We'll use the existing authView to switch modes instead
        setTimeout(() => {
          this.authView?.setMode('login');
        }, 2000);
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (error) {
      handleFetchError(error, 'Password Reset');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Password reset failed:`, { error });
      this.authView?.setError('reset-password', errorMessage);
    }
  }

  private async handleLogout(ctx: ProcessContext): Promise<void> {
    try {
      // Call logout endpoint to clear server-side session
      const token = getJWTToken();
      if (token) {
        try {
          await fetch(AUTH_API_ENDPOINTS.logout, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (error) {
          log.warn('[AuthProcess] Logout API call failed (continuing with local logout)', { error });
        }
      }
    } catch (error) {
      log.warn('[AuthProcess] Error during logout API call (continuing with local logout)', { error });
    }

    // Clear auth data from localStorage and memory
    clearAuthData();

    this.state.isAuthenticated = false;
    this.state.user = null;

    // Clear global user state
    setState('user', {});

    // Close auth view if open
    if (this.authView) {
      bus.emit('process.view.dismiss', { processId: this.id, viewId: this.authView.id });
      this.authView = null;
    }

    // Show login view again
    this.showLogin(ctx, 'handleLogout');

    bus.emit('auth.logout.success', {});
    log.debug(`[AuthProcess] Logged out`);
  }

  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Get JWT token (for API calls)
   */
  getToken(): string | null {
    return getJWTToken();
  }

  async handlePhoneSendCode(phoneNumber: string): Promise<void> {
    log.debug(`[AuthProcess] Sending verification code to ${phoneNumber}`);

    this.state.error = null;
    this.authView?.setError('phone', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.phoneSendCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Phone Verification');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        bus.emit('auth.phone.code.sent', { phoneNumber });
        log.debug(`[AuthProcess] Verification code sent to ${phoneNumber}`);
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      handleFetchError(error, 'Phone Verification');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Send phone code failed:`, { error });
      this.authView?.setError('phone', errorMessage);
    }
  }

  async handlePhoneLogin(phoneNumber: string, code: string): Promise<void> {
    log.debug(`[AuthProcess] Attempting phone login for ${phoneNumber}`);

    this.state.error = null;
    this.authView?.setError('phone', null);

    try {
      const response = await fetch(AUTH_API_ENDPOINTS.phoneLogin, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phoneNumber, code })
      });

      if (!response.ok) {
        const errorMessage = await handleHTTPError(response, 'Phone Login');
        throw new Error(errorMessage || `HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.data) {
        const { token, user } = responseData.data;

        // Save JWT token and user data
        saveJWTToken(token);
        const userData: UserData = {
          email: user.email,
          displayName: user.displayName,
          userId: user.id
        };
        saveUserData(userData);

        this.state.isAuthenticated = true;
        this.state.user = userData;

        // Sync to global state
        setState('user', {
          id: userData.userId,
          email: userData.email,
          displayName: userData.displayName
        });

        // Close auth view after successful login
        if (this.authView) {
          setTimeout(() => {
            bus.emit('process.view.dismiss', { processId: this.id, viewId: this.authView?.id });
            this.authView = null;
          }, 1500);
        }

        this.state.error = null;
        // Show success message briefly before closing
        this.authView?.setSuccess('login', 'Welcome! You are now logged in.');

        bus.emit('auth.login.success', { provider: 'phone' });
        log.debug(`[AuthProcess] Phone login successful`);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      handleFetchError(error, 'Phone Login');
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      log.error(`[AuthProcess] Phone login failed:`, { error });
      bus.emit('auth.login.error' as any, { error: errorMessage } as any);
      this.authView?.setError('phone', errorMessage);
    }
  }
}

type AuthViewMode = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'phone' | 'logged-in';

class AuthView extends BaseViewHTML {
  private mode: AuthViewMode;
  private loginError: string | null = null;
  private loginSuccess: string | null = null;
  private registerError: string | null = null;
  private showPasswordMode: boolean = false; // Default to magic link mode
  private forgotPasswordError: string | null = null;
  private forgotPasswordSuccess: boolean = false;
  private resetPasswordError: string | null = null;
  private resetPasswordSuccess: boolean = false;
  private useEmailAuth: boolean = false;
  private phoneError: string | null = null;
  private phoneCodeSent: boolean = false;
  private phoneNumber: string = '';
  private phoneLoading: boolean = false;
  private clickHandler?: (event: Event) => void;
  private keypressHandlers: Array<{ element: HTMLElement; handler: (event: KeyboardEvent) => void }> = [];

  constructor(
    processId: string,
    viewId: string,
    private authProcess: AuthProcess,
    initialMode: AuthViewMode
  ) {
    super(processId, viewId, {
      showDismissButton: true,
      resizable: false,
      sizes: [1]
    });
    this.mode = initialMode;
  }

  setMode(mode: AuthViewMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    if (mode === 'register') {
      this.useEmailAuth = false;
    }
    this.markDirty();
  }

  setError(target: AuthViewMode, error: string | null): void {
    if (target === 'login') {
      this.loginError = error;
      if (error) {
        this.loginSuccess = null;
      }
    } else if (target === 'register') {
      this.registerError = error;
    } else if (target === 'forgot-password') {
      this.forgotPasswordError = error;
    } else if (target === 'reset-password') {
      this.resetPasswordError = error;
    } else if (target === 'phone') {
      this.phoneError = error;
    }
    if (this.mode === target) {
      this.markDirty();
    }
  }

  setSuccess(target: AuthViewMode, message: string | null): void {
    if (target === 'login') {
      this.loginSuccess = message;
      if (message) {
        this.loginError = null;
      }
    } else if (target === 'register') {
      this.registerError = null; // Clear error if success
      // Could add registerSuccess if needed
    }
    if (this.mode === target && message) {
      this.markDirty();
    }
  }

  setLoginSuccess(message: string | null): void {
    this.setSuccess('login', message);
    if (this.mode === 'login') {
      this.markDirty();
    }
  }

  protected getContentHTML(): string {
    let content: string;
    if (this.mode === 'login') {
      content = this.renderLoginContent();
    } else if (this.mode === 'register') {
      content = this.renderRegisterContent();
    } else if (this.mode === 'forgot-password') {
      content = this.renderForgotPasswordContent();
    } else if (this.mode === 'reset-password') {
      content = this.renderResetPasswordContent();
    } else if (this.mode === 'phone') {
      content = this.renderPhoneContent();
    } else if (this.mode === 'logged-in') {
      content = this.renderLoggedInContent();
    } else {
      content = this.renderLoginContent();
    }

    return `
      <style>
        .auth-view-shell {
          display: flex;
          flex-direction: column;
          height: 100%;
          gap: ${designTokens.padding.lg};
        }
        .auth-view-content {
          display: flex;
          flex-direction: column;
          gap: ${designTokens.padding.sm};
          flex: 1;
        }
        .auth-section {
          display: flex;
          flex-direction: column;
          gap: ${designTokens.padding.sm};
        }
        .auth-social button {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: ${designTokens.padding.md};
        }
        .auth-social-icon {
          width: 30px;
          height: 30px;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .auth-brand {
          margin-top: auto;
          padding-top: ${designTokens.padding.md};
          border-top: 1px solid ${designTokens.borderColor};
          display: flex;
          flex-direction: column;
          gap: ${designTokens.padding.md};
        }
        .auth-brand-header {
          display: flex;
          align-items: center;
          gap: ${designTokens.padding.md};
        }
        .auth-brand-logo-img {
          width: 48px;
          height: 48px;
          object-fit: contain;
          padding: 4px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .auth-brand-title {
          font-size: 20px;
          font-weight: 600;
          color: ${designTokens.contentColor};
          letter-spacing: -0.02em;
        }
        .auth-brand-subtitle {
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColorMuted};
        }
        .auth-email-cta {
          margin: ${designTokens.padding.xs};
          padding: ${designTokens.padding.sm} ${designTokens.padding.sm};
          border-radius: ${designTokens.radius.md};
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.03);
          display: flex;
          flex-direction: column;
          gap: ${designTokens.padding.xs};
          text-align: center;
        }
        .auth-email-cta p {
          margin: 0;
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColorMuted};
        }
      </style>
      <div class="auth-view-shell">
        <div class="auth-view-content">
          ${content}
        </div>
        ${this.mode === 'register' ? this.renderBrandSection() : ''}
      </div>
    `;
  }

  protected render(): void {
    this.cleanupListeners();
    super.render();
    if (!this.element) return;
    this.attachListeners();
  }

  private renderLoginContent(): string {
    const errorDisplay = this.loginError
      ? `<div class="login-error" style="
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(255, 0, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          ">${this.escapeHtml(this.loginError)}</div>`
      : '';
    const successDisplay = this.loginSuccess
      ? `<div class="login-success" style="
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(0, 255, 0, 0.1);
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(0, 128, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">✅ ${this.escapeHtml(this.loginSuccess)}</div>`
      : '';

    const socialButtons = this.renderSocialButtons('login');
    const emailField = this.renderFormField('Email', 'login-email', 'email', 'your@email.com');
    
    // Magic link mode (default)
    const magicLinkButton = createButton('Send Magic Link', {
      className: 'login-magic-link',
      variant: 'primary',
      style: `
        background: rgba(102, 204, 255, 0.1);
        border: 1px solid ${designTokens.borderColorActive};
        box-shadow: 0 0 12px ${designTokens.activeGlow};
        transition: all 0.3s ease;
      `
    });
    const passwordToggle = `<button class="login-password-toggle" type="button" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
            margin-bottom: ${designTokens.padding.sm};
          ">Use password instead</button>`;
    
    // Password mode (optional)
    const passwordField = this.renderFormField('Password', 'login-password', 'password', 'Enter your password');
    const rememberMe = createCheckbox('Remember me', {
      name: 'rememberMe',
      className: 'login-remember'
    });
    const forgotPasswordLink = `<button class="login-forgot-link" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
          ">Forgot password?</button>`;
    const resendVerificationLink = `<button class="login-resend-verification" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
          ">Resend verification email</button>`;
    const loginButton = createButton('Sign In', {
      className: 'login-submit',
      variant: 'primary',
      style: `
        background: rgba(102, 204, 255, 0.1);
        border: 1px solid ${designTokens.borderColorActive};
        box-shadow: 0 0 12px ${designTokens.activeGlow};
        transition: all 0.3s ease;
      `
    });
    const magicLinkToggle = `<button class="login-magic-link-toggle" type="button" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
            margin-bottom: ${designTokens.padding.sm};
          ">Use magic link instead</button>`;
    const registerLink = createButton('Don\'t have an account? Sign up', {
      className: 'login-register-link',
      variant: 'ghost'
    });

    if (this.showPasswordMode) {
      // Password mode
      return `
        <div class="auth-section">
          ${createHeading(2, 'Sign In')}
          ${errorDisplay}
          ${successDisplay}
          ${socialButtons}
          ${emailField}
          ${magicLinkToggle}
          ${passwordField}
          <div style="margin: ${designTokens.padding.md} 0; display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">
            <div>${rememberMe}</div>
            <div style="display:flex; gap:${designTokens.padding.md}; flex-wrap: wrap;">
              ${forgotPasswordLink}
              ${resendVerificationLink}
            </div>
          </div>
          ${loginButton}
          <div style="margin-top: ${designTokens.padding.md}; text-align: center;">${registerLink}</div>
        </div>
      `;
    } else {
      // Magic link mode (default)
      return `
        <div class="auth-section">
          ${createHeading(2, 'Sign In')}
          ${errorDisplay}
          ${successDisplay}
          ${socialButtons}
          ${emailField}
          ${passwordToggle}
          ${magicLinkButton}
          <div style="margin-top: ${designTokens.padding.md}; text-align: center;">${registerLink}</div>
        </div>
      `;
    }
  }

  private renderRegisterContent(): string {
    const errorDisplay = this.registerError
      ? `<div class=\"register-error\" style=\"
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(255, 0, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          \">${this.escapeHtml(this.registerError)}</div>`
      : '';

    if (!this.useEmailAuth) {
      const socialButtons = this.renderSocialButtons('register');
      const emailChoice = createButton('Continue with Email', {
        className: 'register-email-choice',
        variant: 'ghost'
      });

      return `
        <div class=\"auth-section\">
          ${createHeading(2, 'Create Account')}
          ${errorDisplay}
          ${socialButtons}
          <div class=\"auth-email-cta\">
            <p>Prefer to start with email instead?</p>
            ${emailChoice}
          </div>
        </div>
      `;
    }

    const socialButtons = this.renderSocialButtons('register');
    const emailField = this.renderFormField('Email', 'register-email', 'email', 'your@email.com');
    
    // Magic link mode (default)
    const magicLinkButton = createButton('Send Magic Link', {
      className: 'register-magic-link',
      variant: 'primary',
      style: `
        background: rgba(102, 204, 255, 0.1);
        border: 1px solid ${designTokens.borderColorActive};
        box-shadow: 0 0 12px ${designTokens.activeGlow};
        transition: all 0.3s ease;
      `
    });
    const passwordToggle = `<button class="register-password-toggle" type="button" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
            margin-bottom: ${designTokens.padding.sm};
          ">Use password instead</button>`;
    
    // Password mode (optional)
    const passwordField = this.renderFormField('Password', 'register-password', 'password', 'Create a password');
    const confirmField = this.renderFormField(
      'Confirm Password',
      'register-confirm-password',
      'password',
      'Confirm your password'
    );
    const termsCheckbox = createCheckbox('I agree to the Terms of Service and Privacy Policy', {
      name: 'terms',
      className: 'register-terms'
    });
    const registerButton = createButton('Create Account', {
      className: 'register-submit',
      variant: 'primary'
    });
    const magicLinkToggle = `<button class="register-magic-link-toggle" type="button" style="
            background: none;
            border: none;
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
            margin-bottom: ${designTokens.padding.sm};
          ">Use magic link instead</button>`;
    const loginLink = createButton('Already have an account? Sign in', {
      className: 'register-login-link',
      variant: 'ghost'
    });

    if (this.showPasswordMode) {
      // Password mode
      return `
        <div class=\"auth-section\">
          ${createHeading(2, 'Create Account')}
          ${errorDisplay}
          ${socialButtons}
          ${emailField}
          ${magicLinkToggle}
          ${passwordField}
          ${confirmField}
          <div style=\"margin: ${designTokens.padding.md} 0;\">${termsCheckbox}</div>
          ${registerButton}
          <div style=\"margin-top: ${designTokens.padding.md}; text-align: center;\">${loginLink}</div>
        </div>
      `;
    } else {
      // Magic link mode (default)
      return `
        <div class=\"auth-section\">
          ${createHeading(2, 'Create Account')}
          ${errorDisplay}
          ${socialButtons}
          ${emailField}
          ${passwordToggle}
          ${magicLinkButton}
          <div style=\"margin-top: ${designTokens.padding.md}; text-align: center;\">${loginLink}</div>
        </div>
      `;
    }
  }

  private renderForgotPasswordContent(): string {
    const errorDisplay = this.forgotPasswordError
      ? `<div class=\"forgot-password-error\" style=\"
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(255, 0, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          \">${this.escapeHtml(this.forgotPasswordError)}</div>`
      : '';

    const successDisplay = this.forgotPasswordSuccess
      ? `<div class=\"forgot-password-success\" style=\"
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(0, 255, 0, 0.1);
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(0, 255, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          \">If an account with that email exists, a password reset link has been sent.</div>`
      : '';

    const emailField = this.renderFormField('Email', 'forgot-password-email', 'email', 'your@email.com');
    const submitButton = createButton('Send Reset Link', {
      className: 'forgot-password-submit',
      variant: 'primary'
    });
    const backLink = createButton('Back to login', {
      className: 'forgot-password-back-link',
      variant: 'ghost'
    });

    return `
      <div class=\"auth-section\">
        ${createHeading(2, 'Reset Password')}
        ${errorDisplay}
        ${successDisplay}
        <p style=\"color: ${designTokens.contentColorMuted}; margin-bottom: ${designTokens.padding.md};\">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        ${emailField}
        ${submitButton}
        <div style=\"margin-top: ${designTokens.padding.md}; text-align: center;\">${backLink}</div>
      </div>
    `;
  }

  private renderResetPasswordContent(): string {
    const errorDisplay = this.resetPasswordError
      ? `<div class=\"reset-password-error\" style=\"
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(255, 0, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          \">${this.escapeHtml(this.resetPasswordError)}</div>`
      : '';

    const successDisplay = this.resetPasswordSuccess
      ? `<div class=\"reset-password-success\" style=\"
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(0, 255, 0, 0.1);
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(0, 255, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          \">Password has been reset successfully. You can now log in with your new password.</div>`
      : '';

    const passwordField = this.renderFormField('New Password', 'reset-password-password', 'password', 'Enter new password');
    const confirmField = this.renderFormField('Confirm Password', 'reset-password-confirm', 'password', 'Confirm new password');
    const submitButton = createButton('Reset Password', {
      className: 'reset-password-submit',
      variant: 'primary'
    });
    const backLink = createButton('Back to login', {
      className: 'reset-password-back-link',
      variant: 'ghost'
    });

    return `
      <div class=\"auth-section\">
        ${createHeading(2, 'Set New Password')}
        ${errorDisplay}
        ${successDisplay}
        ${passwordField}
        ${confirmField}
        ${submitButton}
        <div style=\"margin-top: ${designTokens.padding.md}; text-align: center;\">${backLink}</div>
      </div>
    `;
  }

  private renderSocialButtons(prefix: 'login' | 'register'): string {
    return `
      <div class=\"${prefix}-social\" style=\"
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${designTokens.padding.sm};
        margin-bottom: ${designTokens.padding.md};
      \">
        ${this.renderSocialButton(`${prefix}-social-x`, 'x', 'Continue with X')}
        ${this.renderSocialButton(`${prefix}-social-github`, 'github', 'Continue with GitHub')}
        ${this.renderSocialButton(`${prefix}-social-google`, 'google', 'Continue with Google')}
        ${this.renderSocialButton(`${prefix}-social-microsoft`, 'microsoft', 'Continue with Microsoft')}
        ${this.renderSocialButton(`${prefix}-social-phone`, 'phone', 'Continue with Phone')}
        <div style=\"display:flex;align-items:center;margin-top:15px;gap:${designTokens.padding.md};\">
          <div style=\"flex:1;height:1px;background:${designTokens.borderColor};\"></div>
          <span style=\"color:${designTokens.contentColorMuted};font-size:${designTokens.fontSize.sm};\">or</span>
          <div style=\"flex:1;height:1px;background:${designTokens.borderColor};\"></div>
        </div>
      </div>
    `;
  }

  private renderSocialButton(className: string, provider: string, label: string): string {
    const { icon, accent } = this.getProviderIcon(provider);
    const iconLeft = '24%';
    const textLeft = '38%';
    return `
      <button class=\"${className}\" data-provider=\"${provider}\" style=\"
        position:relative;
        width:100%;
        padding:${designTokens.padding.md};
        padding-left:${textLeft};
        background:rgba(255,255,255,0.08);
        border:1px solid ${designTokens.borderColor};
        border-radius:${designTokens.radius.sm};
        color:${designTokens.contentColor};
        font-size:${designTokens.fontSize.sm};
        font-weight:500;
        cursor:pointer;
        transition:all 0.2s;
        text-align:left;
      \">
        <span class=\"auth-social-icon\" style=\"
          position:absolute;
          left:${iconLeft};
          top:50%;
          transform:translateY(-50%);
          background:${accent};
        \">
          ${icon}
        </span>
        ${label}
      </button>
    `;
  }

  private renderFormField(
    label: string,
    className: string,
    type: 'email' | 'password' | 'text' | 'textarea',
    placeholder: string
  ): string {
    return createFormField(
      label,
      createTextInput({
        type,
        name: className,
        placeholder,
        required: true,
        className
      })
    );
  }

  private renderBrandSection(): string {
    return `
      <div class=\"auth-brand\">
        <div class=\"auth-brand-header\">
          <img src=\"/logo.png\" alt=\"Pivot logo\" class=\"auth-brand-logo-img\" />
          <div>
            <div class=\"auth-brand-title\">pivot.is</div>
            <div class=\"auth-brand-subtitle\">The Decision Engine</div>
          </div>
        </div>
        <p style=\"margin:0; color:${designTokens.contentColorMuted}; line-height:1.6;\">
          Pivot is the world's first decision engine designed to proactively make you healthier, more intelligent,
          and more of a participant in your own life. It's not a chatbot—and there is no chat box. Pivot isn't about
          generating words or hoarding documents you'll never act on. It's about growing in life and seeing the plan
          before you need it.
        </p>
      </div>
    `;
  }

  private renderLoggedInContent(): string {
    const userData = getUserData();
    const email = userData?.email || 'Unknown';
    const displayName = userData?.displayName || email.split('@')[0];

    return `
      <div class="auth-section" style="
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${designTokens.padding.xl};
        padding: ${designTokens.padding.xl};
        text-align: center;
      ">
        ${createHeading(2, 'You\'re logged in!')}
        
        <div style="
          display: flex;
          flex-direction: column;
          gap: ${designTokens.padding.md};
          width: 100%;
          max-width: 400px;
        ">
          <div style="
            padding: ${designTokens.padding.lg};
            background: rgba(0, 255, 0, 0.05);
            border: 1px solid rgba(0, 255, 0, 0.2);
            border-radius: ${designTokens.radius.md};
          ">
            <div style="
              font-size: ${designTokens.fontSize.lg};
              font-weight: 600;
              color: ${designTokens.contentColor};
              margin-bottom: ${designTokens.padding.sm};
            ">${this.escapeHtml(displayName)}</div>
            <div style="
              font-size: ${designTokens.fontSize.sm};
              color: ${designTokens.contentColorMuted};
            ">${this.escapeHtml(email)}</div>
          </div>

          ${createButton('Log Out', {
            className: 'auth-logout',
            variant: 'primary',
            style: `
              background: rgba(255, 0, 0, 0.1);
              border: 1px solid rgba(255, 0, 0, 0.3);
              color: rgba(255, 100, 100, 0.9);
              width: 100%;
            `
          })}
        </div>
      </div>
    `;
  }

  private renderPhoneContent(): string {
    const errorDisplay = this.phoneError
      ? `<div class="phone-error" style="
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(255, 0, 0, 0.1);
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(255, 0, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          ">${this.escapeHtml(this.phoneError)}</div>`
      : '';

    const successDisplay = this.phoneCodeSent
      ? `<div class="phone-success" style="
            padding: ${designTokens.padding.md};
            margin-bottom: ${designTokens.padding.md};
            background: rgba(0, 255, 0, 0.1);
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: ${designTokens.radius.sm};
            color: rgba(0, 255, 0, 0.9);
            font-size: ${designTokens.fontSize.sm};
          ">Verification code sent! Check your phone.</div>`
      : '';

    if (!this.phoneCodeSent) {
      // Step 1: Enter phone number
      const phoneField = this.renderFormField('Phone Number', 'phone-number', 'text', '+1234567890');
      const sendCodeButton = createButton(
        this.phoneLoading ? 'Sending...' : 'Send Verification Code',
        {
          className: 'phone-send-code',
          variant: 'primary',
          disabled: this.phoneLoading
        }
      );
      const backLink = createButton('Back to login', {
        className: 'phone-back-link',
        variant: 'ghost'
      });

      return `
        <div class="auth-section">
          ${createHeading(2, 'Sign In with Phone')}
          ${errorDisplay}
          <p style="color: ${designTokens.contentColorMuted}; margin-bottom: ${designTokens.padding.md};">
            Enter your phone number to receive a verification code via SMS.
          </p>
          ${phoneField}
          ${sendCodeButton}
          <div style="margin-top: ${designTokens.padding.md}; text-align: center;">${backLink}</div>
        </div>
      `;
    } else {
      // Step 2: Enter verification code
      const codeField = this.renderFormField('Verification Code', 'phone-code', 'text', '123456');
      const loginButton = createButton(
        this.phoneLoading ? 'Verifying...' : 'Sign In',
        {
          className: 'phone-login',
          variant: 'primary',
          disabled: this.phoneLoading
        }
      );
      const resendButton = createButton('Resend Code', {
        className: 'phone-resend-code',
        variant: 'ghost'
      });

      return `
        <div class="auth-section">
          ${createHeading(2, 'Enter Verification Code')}
          ${errorDisplay}
          ${successDisplay}
          <p style="color: ${designTokens.contentColorMuted}; margin-bottom: ${designTokens.padding.md};">
            Enter the 6-digit code sent to ${this.escapeHtml(this.phoneNumber)}
          </p>
          ${codeField}
          ${loginButton}
          <div style="margin-top: ${designTokens.padding.md}; text-align: center;">${resendButton}</div>
        </div>
      `;
    }
  }

  private getProviderIcon(provider: string): { icon: string; accent: string } {
    switch (provider) {
      case 'phone':
        return {
          accent: '#10B981',
          icon: `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          `
        };
      case 'github':
        return {
          accent: 'rgba(255,255,255,0.06)',
          icon: `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path fill="currentColor" d="M12 0.5C5.373 0.5 0 5.873 0 12.5C0 17.915 3.438 22.407 8.205 23.978C8.805 24.088 9.025 23.725 9.025 23.405C9.025 23.118 9.015 22.318 9.01 21.268C5.672 21.963 4.968 19.725 4.968 19.725C4.422 18.363 3.633 17.998 3.633 17.998C2.546 17.255 3.717 17.27 3.717 17.27C4.922 17.355 5.555 18.505 5.555 18.505C6.626 20.345 8.364 19.805 9.05 19.505C9.159 18.73 9.468 18.205 9.81 17.915C7.147 17.625 4.343 16.57 4.343 11.805C4.343 10.465 4.819 9.365 5.607 8.495C5.482 8.205 5.06 6.93 5.722 5.205C5.722 5.205 6.747 4.895 8.997 6.358C9.986 6.08 11.046 5.94 12.106 5.935C13.166 5.94 14.226 6.08 15.215 6.358C17.465 4.895 18.49 5.205 18.49 5.205C19.152 6.93 18.73 8.205 18.605 8.495C19.393 9.365 19.869 10.465 19.869 11.805C19.869 16.58 17.06 17.62 14.39 17.905C14.82 18.275 15.205 19.015 15.205 20.155C15.205 21.795 15.19 23.035 15.19 23.405C15.19 23.725 15.41 24.093 16.02 23.978C20.787 22.403 24.225 17.915 24.225 12.5C24.225 5.873 18.852 0.5 12.225 0.5H12Z"/>
            </svg>
          `
        };
      case 'google':
        return {
          accent: '#1A73E8',
          icon: `
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#fff" d="M21.35 11.1H12.2V13.9H17.9C17.65 15.25 16.9 16.35 15.8 17.1V19.45H19C20.95 17.65 22.1 15.05 22.1 12.1C22.1 11.4 22 10.75 21.85 10.1L21.35 11.1Z"/>
              <path fill="#fff" d="M12.2 21.5C15.15 21.5 17.6 20.55 19 19.45L15.8 17.1C15 17.65 13.9 18 12.2 18C9.35 18 6.95 16.15 6.1 13.6H2.8V16.05C4.25 19.35 7.9 21.5 12.2 21.5Z"/>
              <path fill="#fff" d="M6.1 13.6C5.9 13.05 5.8 12.5 5.8 11.9C5.8 11.3 5.9 10.75 6.1 10.2V7.75H2.8C2.1 9.1 1.7 10.55 1.7 12.1C1.7 13.65 2.1 15.1 2.8 16.45L6.1 13.6Z"/>
              <path fill="#fff" d="M12.2 6.2C14.2 6.2 15.75 6.9 16.85 7.9L19.1 5.65C17.6 4.25 15.15 3.3 12.2 3.3C7.9 3.3 4.25 5.45 2.8 8.75L6.1 11.2C6.95 8.65 9.35 6.2 12.2 6.2Z"/>
            </svg>
          `
        };
      case 'x':
        return {
          accent: '#0f1419',
          icon: `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path fill="currentColor" d="M18.244 3.5h2.963l-6.476 7.4 7.634 9.6H16.96l-4.604-6.01L7.011 20.5H4.046l6.91-7.9L3.605 3.5H8.14l4.146 5.527L18.244 3.5Zm-1.04 15.2h1.64L7.876 5.8H6.138l11.066 12.9Z"/>
            </svg>
          `
        };
      case 'microsoft':
        return {
          accent: '#F87171',
          icon: `
            <svg width="18" height="18" viewBox="0 0 24 24">
              <rect x="2" y="2" width="9" height="9" fill="#F35325"/>
              <rect x="13" y="2" width="9" height="9" fill="#81BC06"/>
              <rect x="2" y="13" width="9" height="9" fill="#05A6F0"/>
              <rect x="13" y="13" width="9" height="9" fill="#FFBA08"/>
            </svg>
          `
        };
      default:
        return {
          accent: 'rgba(255,255,255,0.08)',
          icon: `<span style="font-weight:700;color:${designTokens.contentColor};">${provider.slice(0, 1).toUpperCase()}</span>`
        };
    }
  }

  private attachListeners(): void {
    if (!this.element) return;

    this.clickHandler = (event: Event) => {
      const target = event.target as HTMLElement;

      if (target.closest('[data-provider]')) {
        event.preventDefault();
        const provider = target.closest('[data-provider]')?.getAttribute('data-provider');
        if (provider === 'google' || provider === 'microsoft' || provider === 'github' || provider === 'x') {
          this.authProcess.handleOAuthLogin(provider as 'google' | 'microsoft' | 'github' | 'x');
        } else if (provider === 'phone') {
          this.setMode('phone');
        } else if (provider) {
          bus.emit('auth.social.login', { provider });
        }
        return;
      }

      if (target.closest('.login-register-link')) {
        event.preventDefault();
        bus.emit('auth.register.required', { source: 'auth-view:register-link' });
        return;
      }

      if (target.closest('.register-login-link')) {
        event.preventDefault();
        bus.emit('auth.login.required', { source: 'auth-view:login-link' });
        return;
      }

      if (target.closest('.login-forgot-link')) {
        event.preventDefault();
        this.setMode('forgot-password');
        return;
      }

      if (target.closest('.login-resend-verification')) {
        event.preventDefault();
        const email = window.prompt('Enter the email used for your account');
        if (email) {
          this.authProcess.handleResendVerification(email.trim());
        }
        return;
      }

      if (target.closest('.forgot-password-back-link')) {
        event.preventDefault();
        this.setMode('login');
        return;
      }

      if (target.closest('.reset-password-back-link')) {
        event.preventDefault();
        this.setMode('login');
        return;
      }

      if (target.closest('.phone-back-link')) {
        event.preventDefault();
        this.setMode('login');
        return;
      }

      if (target.closest('.phone-resend-code')) {
        event.preventDefault();
        this.phoneCodeSent = false;
        this.markDirty();
        this.render();
        return;
      }

      if (target.closest('.forgot-password-submit')) {
        event.preventDefault();
        this.submitForgotPassword();
        return;
      }

      if (target.closest('.reset-password-submit')) {
        event.preventDefault();
        this.submitResetPassword();
        return;
      }

      if (target.closest('.register-email-choice')) {
        event.preventDefault();
        this.useEmailAuth = true;
        this.markDirty();
        this.render();
        return;
      }

      if (target.closest('.login-submit')) {
        event.preventDefault();
        this.submitLogin();
        return;
      }

      if (target.closest('.register-magic-link')) {
        event.preventDefault();
        this.submitRegisterMagicLink();
        return;
      }

      if (target.closest('.login-magic-link')) {
        event.preventDefault();
        this.submitMagicLink();
        return;
      }

      if (target.closest('.register-password-toggle')) {
        event.preventDefault();
        this.showPasswordMode = true;
        this.render();
        return;
      }

      if (target.closest('.register-magic-link-toggle')) {
        event.preventDefault();
        this.showPasswordMode = false;
        this.render();
        return;
      }

      if (target.closest('.register-submit')) {
        event.preventDefault();
        this.submitRegister();
        return;
      }

      if (target.closest('.phone-send-code')) {
        event.preventDefault();
        this.submitPhoneSendCode();
        return;
      }

      if (target.closest('.phone-login')) {
        event.preventDefault();
        this.submitPhoneLogin();
        return;
      }

      if (target.closest('.auth-logout')) {
        event.preventDefault();
        bus.emit('auth.logout', {});
        return;
      }
    };

    this.element.addEventListener('click', this.clickHandler);

    const loginEmail = this.element.querySelector('.login-email') as HTMLInputElement | null;
    const loginPassword = this.element.querySelector('.login-password') as HTMLInputElement | null;
    const magicLinkButton = this.element.querySelector('.login-magic-link') as HTMLButtonElement | null;
    
    if (loginEmail) {
      if (this.showPasswordMode && loginPassword) {
        // Password mode: Enter submits login
        const handler = (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            this.submitLogin();
          }
        };
        loginEmail.addEventListener('keypress', handler);
        loginPassword.addEventListener('keypress', handler);
        this.keypressHandlers.push({ element: loginEmail, handler });
        this.keypressHandlers.push({ element: loginPassword, handler });
      } else if (!this.showPasswordMode) {
        // Magic link mode: Enter submits magic link
        const handler = (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            this.submitMagicLink();
          }
        };
        loginEmail.addEventListener('keypress', handler);
        this.keypressHandlers.push({ element: loginEmail, handler });
      }
    }
    
    // Handle Enter key on magic link button (when in magic link mode)
    if (magicLinkButton && !this.showPasswordMode) {
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          this.submitMagicLink();
        }
      };
      magicLinkButton.addEventListener('keypress', handler);
      this.keypressHandlers.push({ element: magicLinkButton, handler });
    }

    const registerInputs = [
      '.register-email',
      '.register-password',
      '.register-confirm-password'
    ];
    registerInputs.forEach(selector => {
      const input = this.element?.querySelector(selector) as HTMLInputElement | null;
      if (input) {
        const handler = (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            this.submitRegister();
          }
        };
        input.addEventListener('keypress', handler);
        this.keypressHandlers.push({ element: input, handler });
      }
    });
  }

  private cleanupListeners(): void {
    if (this.element && this.clickHandler) {
      this.element.removeEventListener('click', this.clickHandler);
    }
    for (const { element, handler } of this.keypressHandlers) {
      element.removeEventListener('keypress', handler);
    }
    this.keypressHandlers = [];
  }

  private submitMagicLink(): void {
    if (!this.element) return;
    const emailInput = this.element.querySelector('.login-email') as HTMLInputElement | null;
    this.loginSuccess = null;

    if (emailInput) {
      const email = emailInput.value.trim();
      if (email) {
        this.authProcess.handleMagicLinkSend(email);
      } else {
        this.setError('login', 'Please enter your email address');
      }
    }
  }

  private submitLogin(): void {
    if (!this.element) return;
    const emailInput = this.element.querySelector('.login-email') as HTMLInputElement | null;
    const passwordInput = this.element.querySelector('.login-password') as HTMLInputElement | null;
    const rememberCheckbox = this.element.querySelector('.login-remember') as HTMLInputElement | null;
    this.loginSuccess = null;

    if (emailInput && passwordInput) {
      const email = emailInput.value.trim();
      const password = passwordInput.value;
      const rememberMe = rememberCheckbox?.checked || false;
      if (email && password) {
        this.authProcess.handleLogin(email, password, rememberMe);
      } else {
        this.setError('login', 'Please enter your email and password');
      }
    }
  }

  private submitRegisterMagicLink(): void {
    if (!this.element) return;
    const emailInput = this.element.querySelector('.register-email') as HTMLInputElement | null;
    this.registerError = null;

    if (emailInput) {
      const email = emailInput.value.trim();
      if (email) {
        this.authProcess.handleMagicLinkSend(email);
      } else {
        this.setError('register', 'Please enter your email address');
      }
    }
  }

  private submitRegister(): void {
    if (!this.element) return;
    const emailInput = this.element.querySelector('.register-email') as HTMLInputElement | null;
    const passwordInput = this.element.querySelector('.register-password') as HTMLInputElement | null;
    const confirmPasswordInput = this.element.querySelector('.register-confirm-password') as HTMLInputElement | null;
    const termsCheckbox = this.element.querySelector('.register-terms') as HTMLInputElement | null;

    if (emailInput && passwordInput && confirmPasswordInput) {
      const email = emailInput.value.trim();
      const password = passwordInput.value;
      const confirmPassword = confirmPasswordInput.value;
      const termsAccepted = termsCheckbox?.checked || false;

      if (!email || !password || !confirmPassword) {
        this.setError('register', 'Please fill in all fields');
        return;
      }

      if (!termsAccepted) {
        this.setError('register', 'Please accept the Terms of Service and Privacy Policy');
        return;
      }

      this.authProcess.handleRegister(email, password, confirmPassword);
    }
  }

  private submitForgotPassword(): void {
    if (!this.element) return;
    const emailInput = this.element.querySelector('.forgot-password-email') as HTMLInputElement | null;

    if (emailInput) {
      const email = emailInput.value.trim();
      if (email) {
        this.forgotPasswordError = null;
        this.forgotPasswordSuccess = false;
        this.markDirty();
        this.authProcess.handleForgotPassword(email);
      } else {
        this.setError('forgot-password', 'Please enter your email address');
      }
    }
  }

  private submitResetPassword(): void {
    if (!this.element) return;
    const passwordInput = this.element.querySelector('.reset-password-password') as HTMLInputElement | null;
    const confirmPasswordInput = this.element.querySelector('.reset-password-confirm') as HTMLInputElement | null;

    if (passwordInput && confirmPasswordInput) {
      const password = passwordInput.value;
      const confirmPassword = confirmPasswordInput.value;

      if (!password || !confirmPassword) {
        this.setError('reset-password', 'Please fill in all fields');
        return;
      }

      if (password !== confirmPassword) {
        this.setError('reset-password', 'Passwords do not match');
        return;
      }

      // Get token from URL (using reset_token parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('reset_token');

      if (!token) {
        this.setError('reset-password', 'Invalid reset token');
        return;
      }

      this.resetPasswordError = null;
      this.resetPasswordSuccess = false;
      this.markDirty();
      this.authProcess.handleResetPassword(token, password);
    }
  }

  private submitPhoneSendCode(): void {
    if (!this.element) return;
    
    // Prevent duplicate requests
    if (this.phoneLoading) {
      return;
    }
    
    const phoneInput = this.element.querySelector('.phone-number') as HTMLInputElement | null;

    if (phoneInput) {
      const phoneNumber = phoneInput.value.trim();
      if (phoneNumber) {
        // Normalize phone number (remove spaces, dashes, etc.)
        const normalized = phoneNumber.replace(/[^\d+]/g, '');
        
        // Validate E.164 format (basic check - server will normalize)
        if (!/^\+?[1-9]\d{1,14}$/.test(normalized)) {
          this.setError('phone', 'Please enter a valid phone number (e.g., +1234567890 or 1234567890)');
          return;
        }
        
        this.phoneNumber = normalized.startsWith('+') ? normalized : `+1${normalized}`;
        this.phoneError = null;
        this.phoneCodeSent = false;
        this.phoneLoading = true;
        this.markDirty();
        this.render();
        
        this.authProcess.handlePhoneSendCode(this.phoneNumber).then(() => {
          this.phoneCodeSent = true;
          this.phoneLoading = false;
          this.markDirty();
          this.render();
        }).catch((error) => {
          this.phoneLoading = false;
          this.markDirty();
          this.render();
        });
      } else {
        this.setError('phone', 'Please enter your phone number');
      }
    }
  }

  private submitPhoneLogin(): void {
    if (!this.element) return;
    
    // Prevent duplicate requests
    if (this.phoneLoading) {
      return;
    }
    
    const codeInput = this.element.querySelector('.phone-code') as HTMLInputElement | null;

    if (codeInput) {
      const code = codeInput.value.trim().replace(/\D/g, ''); // Remove non-digits
      if (code && code.length === 6) {
        this.phoneError = null;
        this.phoneLoading = true;
        this.markDirty();
        this.render();
        
        this.authProcess.handlePhoneLogin(this.phoneNumber, code).catch(() => {
          this.phoneLoading = false;
          this.markDirty();
          this.render();
        });
      } else {
        this.setError('phone', 'Please enter the 6-digit verification code');
      }
    }
  }

  protected escapeHtml(text: string | null): string {
    if (!text) return '';
    return escapeHtmlUtil(text);
  }
}

registerProcess('auth', (id: string) => new AuthProcess(id));

